import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntityTypes } from '@guryou/redux';
import { parseDate } from '@guryou/utils/build/dateTimeUtils';

const initFilterState = {
  entityType: 'all',
  providerId: 'all',
};

const CalendarFilter = ({
  filterState,
  setFilterState,
  selectedPeriod,
  setSelectedPeriod,
  setDefaultCalendarView,
  providerOptions = [],
  lockProviderId = null,
  onClose,
}) => {
  const { t } = useTranslation();
  const [dateInput, setDateInput] = useState(selectedPeriod.format('YYYY-MM-DD'));

  // Keep date input synced with external selectedPeriod changes
  useEffect(() => {
    setDateInput(selectedPeriod.format('YYYY-MM-DD'));
  }, [selectedPeriod]);

  const entityTypeOptionsRaw = useEntityTypes({ showAll: true, showVideos: false });
  const entityTypeOptions = entityTypeOptionsRaw || [];

  const handleDateChange = e => {
    const dateValue = e.target.value;
    setDateInput(dateValue);
    if (dateValue) {
      const parsedDate = parseDate(dateValue);
      setSelectedPeriod(parsedDate);
      setDefaultCalendarView('day');
    }
  };

  const onFilterChange = (field, value) => {
    if (field === 'providerId' && lockProviderId) {
      return;
    }
    setFilterState(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const hasActiveFilters = filterState.entityType !== 'all' || (!lockProviderId && filterState.providerId !== 'all');

  const handleReset = () => {
    setFilterState({
      ...initFilterState,
      providerId: lockProviderId || 'all',
    });
    setDateInput(selectedPeriod.format('YYYY-MM-DD'));
  };

  return (
    <div className="h-full flex flex-col">
      {/* Filters Content */}
      <div className="flex-1 space-y-6">
        {/* Date Filter */}
        <div>
          <label htmlFor="date-filter" className="block mb-2.5 text-sm font-semibold text-gray-700">
            {t('GENERAL.date', 'Data')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <input
              type="date"
              id="date-filter"
              value={dateInput}
              onChange={handleDateChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-feel-primary focus:border-feel-primary block w-full pl-11 pr-3 py-2.5 transition-all"
            />
          </div>
        </div>

        {/* Entity Type Filter */}
        <div>
          <label htmlFor="entity-type-filter" className="block mb-2.5 text-sm font-semibold text-gray-700">
            {t('CALENDAR.entityType', 'Tipo di Entità')}
          </label>
          <select
            id="entity-type-filter"
            value={filterState.entityType || 'all'}
            onChange={e => onFilterChange('entityType', e.target.value)}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-feel-primary focus:border-feel-primary block w-full p-2.5 transition-all cursor-pointer"
          >
            {entityTypeOptions && entityTypeOptions.length > 0 ? (
              entityTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            ) : (
              <option value="all">Tutti</option>
            )}
          </select>
        </div>

        {/* Provider filter: hidden when fixed by widget auth login */}
        {!lockProviderId && providerOptions.length > 1 && (
          <div>
            <label htmlFor="provider-filter" className="block mb-2.5 text-sm font-semibold text-gray-700">
              {t('GENERAL.provider', 'Provider')}
            </label>
            <select
              id="provider-filter"
              value={filterState.providerId || 'all'}
              onChange={e => onFilterChange('providerId', e.target.value)}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-feel-primary focus:border-feel-primary block w-full p-2.5 transition-all cursor-pointer"
            >
              {providerOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Reset Button */}
      {hasActiveFilters && (
        <div className="mt-6 pt-5 border-t border-gray-200">
          <button
            type="button"
            onClick={() => {
              handleReset();
              if (onClose) {
                onClose();
              }
            }}
            className="w-full text-white bg-gray-600 hover:bg-gray-700 focus:ring-4 focus:outline-none focus:ring-gray-300 font-semibold rounded-lg text-sm px-5 py-3 text-center transition-all duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {t('GENERAL.resetFilter', 'Reimposta Filtri')}
          </button>
        </div>
      )}

      {/* Apply Button (for mobile/drawer) */}
      {onClose && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="w-full text-white bg-feel-primary hover:bg-feel-primary-dark focus:ring-4 focus:outline-none focus:ring-feel-primary/50 font-semibold rounded-lg text-sm px-5 py-3 text-center transition-all duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
          >
            {t('GENERAL.applyFilters', 'Applica filtri')}
          </button>
        </div>
      )}
    </div>
  );
};

export default CalendarFilter;
export { initFilterState };
